/**
 * Hooks for billing: page data, payouts, statements
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { billingApi } from '@/services/api/billing';

/**
 * Single hook that fetches all billing page data in one request.
 * Returns plans, account, overview, and payouts.
 */
export function useBillingPageData() {
  return useQuery({
    queryKey: ['billing-page-data'],
    queryFn: () => billingApi.getPageData(),
    staleTime: 0, // Immer neu laden (Portal/Domain-Counts können sich ändern)
  });
}

export function useBillingStatements() {
  return useQuery({
    queryKey: ['billing-statements'],
    queryFn: () => billingApi.getStatements(),
  });
}

export function useUpdateBillingAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ planSlug, tierId }: { planSlug: string; tierId?: number }) =>
      billingApi.updateAccount(planSlug, tierId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing-account'] });
      queryClient.invalidateQueries({ queryKey: ['billing-overview'] });
    },
  });
}

export function useRequestPayout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => billingApi.requestPayout(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payouts'] });
      queryClient.invalidateQueries({ queryKey: ['billing-overview'] });
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
    },
  });
}
